import React, { useContext, useEffect } from 'react';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { isAdmin, isManager } from '../../../common/actionHelper';
import { useCaseLock } from '../../../common/caseLocking/CaseLockContext';
import { TitleSmall } from '../../../common/components/v2';
import { Resource } from '../../../common/constants/resource';
import { NoraPermissionV2 } from '../../../common/constants/roles';
import ElementLocators from '../../../common/ElementLocators';
import { getFormattedTimestamp } from '../../../common/helpers';
import { useAccess } from '../../../common/hooks/useAccess';
import ConfirmationDeleteButton from '../../layout/ConfirmationDeleteButton';
import { SnackBarType } from '../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { ITask } from '../../tasks/interfaces/ITask';
import { getAllTasks } from '../../tasks/slice/taskListSlice';
import { TaskTypes } from '../../tasks/types/taskTypes';
import { requestOpen } from '../drawer/drawerCoordSlice';
import { INote } from '../interfaces/INote';
import styles from './notesDrawer.module.scss';
import { deleteNote, getNotes } from './notesSlice';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const InlineNote: React.FC<{ note: INote }> = ({ note }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { id: applicationId } = useParams<{ id: string }>();
  const canDeleteNote = useAccess({
    permission: NoraPermissionV2.DELETE_NOTES,
    legacyCheck: roles => isAdmin(roles) || isManager(roles),
  });
  return (
    <Box
      sx={{
        bgcolor: '#f5f5f3',
        borderRadius: '8px',
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography
          component="span"
          sx={{ fontSize: 11, fontWeight: 500, color: '#6b6b67' }}
        >
          {note.userName}
        </Typography>
        <Box display="flex" alignItems="center" gap={0}>
          <Typography component="span" sx={{ fontSize: 11, color: '#9b9b96' }}>
            {getFormattedTimestamp(note.date, 'MMM D, YYYY · h:mm A')}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              '& .MuiButtonBase-root': {
                minWidth: 0,
                minHeight: 0,
                lineHeight: 1,
                padding: '4px',
                color: '#9b9b96',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#6b6b67',
                },
              },
            }}
          >
            <ConfirmationDeleteButton
              canDelete={canDeleteNote}
              dialogTitle={Resource.DELETE_NOTE}
              dialogContentText={Resource.DELETE_NOTE_CONFIRMATION_TEXT}
              deleteHandler={() =>
                applicationId &&
                dispatch(deleteNote(applicationId, note.id.toString()))
              }
            />
          </Box>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 13,
          color: '#1a1a18',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}
      >
        {note.note}
      </Typography>
      {note.mibCodes?.length > 0 && (
        <Box className={styles.MibCodesSection}>
          <Box className={styles.MibCodesLabel}>MIB Codeback Draft:</Box>
          <Box className={styles.MibCodesTags}>
            {note.mibCodes.map(code => (
              <Box key={code} component="span" className={styles.MibCodeTag}>
                {code}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const InlineNotesList: React.FC = () => {
  const notes: INote[] = useSelector(state => state.notes.notes);
  const sorted = [...notes].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  if (sorted.length === 0) {
    return (
      <Typography
        sx={{ fontSize: 12, color: '#9b9b96', textAlign: 'center', py: 2 }}
      >
        No notes submitted yet.
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {sorted.map(note => (
        <InlineNote key={note.id} note={note} />
      ))}
    </Stack>
  );
};

export const Notes = ({ applicationId }: { applicationId: string }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { openSnack } = useContext(SnackbarContext);
  const { isLockedForActions } = useCaseLock();
  const canBypassNotesLock = useAccess({
    permission: NoraPermissionV2.CAN_ADD_NOTES_ON_LOCKED_CASE,
  });
  const canAddNote = !isLockedForActions || canBypassNotesLock;
  useEffect(() => {
    dispatch(getNotes(applicationId));
  }, [applicationId, dispatch]);

  const handleAddNote = () => {
    dispatch(requestOpen({ type: 'notes' }));
  };

  const {
    notesTask,
    createNoteFailed,
    createNoteError,
    createNoteErrorType,
    fetchNotesFailed,
    fetchNotesError,
    fetchNotesErrorType,
  }: {
    notesTask: ITask | undefined;
    createNoteFailed: boolean;
    createNoteError: string | undefined;
    createNoteErrorType: string | undefined;
    fetchNotesFailed: boolean;
    fetchNotesError: string | undefined;
    fetchNotesErrorType: string | undefined;
  } = useSelector((state: RootState) => {
    return {
      notesTask: getAllTasks(state.taskList).find(
        t => t.taskType === TaskTypes.NOTES,
      ),
      createNoteFailed: state.notes.createNoteFailed,
      createNoteError: state.notes.createNoteError,
      createNoteErrorType: state.notes.createNoteErrorType,
      fetchNotesFailed: state.notes.fetchNotesFailed,
      fetchNotesError: state.notes.fetchNotesError,
      fetchNotesErrorType: state.notes.fetchNotesErrorType,
    };
  }, shallowEqual);

  useEffect(() => {
    if (createNoteFailed) {
      openSnack({
        id: uuidv4(),
        type: (createNoteErrorType ?? SnackBarType.UNKNOWN) as SnackBarType,
        message: createNoteError ?? '',
      });
    } else if (fetchNotesFailed) {
      openSnack({
        id: uuidv4(),
        type: (fetchNotesErrorType ?? SnackBarType.UNKNOWN) as SnackBarType,
        message: fetchNotesError ?? '',
      });
    }
  }, [
    openSnack,
    createNoteErrorType,
    createNoteError,
    createNoteFailed,
    fetchNotesFailed,
    fetchNotesError,
    fetchNotesErrorType,
  ]);

  if (!notesTask) {
    return null;
  }

  return (
    <Box
      data-label={ElementLocators.LOCATOR_NOTES_SECTION}
      data-section-id="Notes"
      id={notesTask.id.toString()}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <TitleSmall style={{ margin: 0, fontWeight: 500 }}>
          {Resource.TASK_TITLE_NOTES}
        </TitleSmall>
        {canAddNote && (
          <Button
            disableRipple
            onClick={handleAddNote}
            startIcon={<FontAwesomeIcon icon={faPlus} fontSize={10} />}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 400,
              color: '#1a1a18',
              bgcolor: '#ffffff',
              border: '0.5px solid rgba(0,0,0,0.22)',
              borderRadius: '8px',
              height: 28,
              minHeight: 0,
              minWidth: 0,
              padding: '0 12px',
              lineHeight: 1,
              '& .MuiButton-startIcon': {
                marginRight: '6px',
                marginLeft: 0,
              },
              '&:hover': {
                bgcolor: '#f5f5f3',
                border: '0.5px solid rgba(0,0,0,0.22)',
              },
            }}
          >
            Add note
          </Button>
        )}
      </Box>
      <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.12)' }} />
      <InlineNotesList />
    </Box>
  );
};
